/**
 * Normalize /port-activity/me or /port-activity/staff API rows into a port_id → status map.
 */
import { isSimConvertedYes, shouldShowActivate } from './simConversion';

export function parseActivityRows(data) {
  let rows = [];
  if (Array.isArray(data)) rows = data;
  else if (Array.isArray(data?.items)) rows = data.items;
  else if (Array.isArray(data?.status)) rows = data.status;

  const map = {};
  const now = Date.now();
  if (!rows.length) return map;

  if ('port_id' in rows[0] && 'status' in rows[0]) {
    rows.forEach((r) => {
      if (!r?.port_id) return;
      map[r.port_id] = normalizeActivityEntry(r, now);
    });
    return map;
  }

  if ('active_port_id' in rows[0]) {
    rows.forEach((r) => {
      if (!r?.active_port_id) return;
      map[r.active_port_id] = {
        status: 'active',
        status_source: 'hardware',
        timestamp: now,
        stale: false,
      };
    });
  }

  return map;
}

function normalizeActivityEntry(r, now = Date.now()) {
  return {
    status: r.status || 'inactive',
    status_source: r.status_source || null,
    timestamp: now,
    stale: !!r.stale,
    cache_age_seconds: r.cache_age_seconds ?? null,
    remaining_seconds: r.remaining_seconds ?? null,
    has_recent_activations: !!r.has_recent_activations,
    show_30s_warning: !!r.show_30s_warning,
    message_triggered: !!r.message_triggered,
    handover: r.status === 'handing_over',
    ui_warmup_until: r.ui_warmup_until || null,
  };
}

/** Remap activity keys when port_id from API does not match local port.id */
export function remapPortStatusForPorts(statusMap, ports = []) {
  if (!ports.length) return statusMap;
  const remapped = {};
  Object.entries(statusMap).forEach(([activityPortId, statusData]) => {
    if (ports.some((p) => String(p.id) === String(activityPortId))) {
      remapped[activityPortId] = statusData;
      return;
    }
    const match = ports.find(
      (p) => String(p.port_number) === String(activityPortId)
    );
    if (match) {
      remapped[match.id] = statusData;
    }
  });
  return remapped;
}

/** Build a PortActivationBadge-compatible entry from batch SMS item fields */
export function activationEntryFromBatchStatus(status) {
  if (!status || status === 'pending') {
    return { status: 'inactive' };
  }
  return { status };
}

/** Shared activate-button disabled/text logic */
export function getActivationButtonState(port, portStatus = {}, portId = port?.id) {
  if (!shouldShowActivate(port)) {
    const converted = isSimConvertedYes(port);
    return {
      disabled: true,
      className: 'btn btn-sm btn-outline-secondary',
      text: converted ? 'Converted' : 'Service',
      title: converted
        ? 'SIM already converted — activation not available'
        : 'Service number — activation not available',
      hide: true,
    };
  }

  const entry = portStatus[portId] || {};
  const status = getEffectiveActivationStatus(entry);

  if (!port?.assigned_number) {
    return {
      disabled: true,
      className: 'btn btn-sm btn-outline-secondary',
      text: 'No Number',
      title: 'No number assigned',
    };
  }
  if (status === 'activating') {
    return {
      disabled: true,
      className: 'btn btn-sm btn-warning',
      text: 'Activating…',
      title: 'Activation in progress',
    };
  }
  if (status === 'active') {
    return {
      disabled: true,
      className: 'btn btn-sm btn-outline-success',
      text: 'Active',
      title: 'This number is already active',
    };
  }
  if (status === 'queued') {
    return {
      disabled: true,
      className: 'btn btn-sm btn-outline-warning',
      text: 'Queued',
      title: 'Waiting in activation queue',
    };
  }
  if (status === 'error') {
    return {
      disabled: false,
      className: 'btn btn-sm btn-outline-danger',
      text: 'Retry',
      title: 'Click to retry activation',
    };
  }
  return {
    disabled: false,
    className: 'btn btn-sm btn-primary',
    text: 'Activate',
    title: 'Activate this number',
  };
}

/**
 * Derive display status from a normalized port status entry.
 */
export function getEffectiveActivationStatus(entry = {}) {
  const baseStatus = entry.status || 'inactive';
  const inWarmup =
    entry.ui_warmup_until &&
    Date.now() < entry.ui_warmup_until &&
    !entry.message_triggered;
  if (inWarmup && baseStatus === 'active') return 'activating';
  if (baseStatus === 'handing_over') return 'handing_over';
  return baseStatus;
}

export function activationStatusTitle(effectiveStatus, entry = {}) {
  const source = entry.status_source
    ? ` (source: ${entry.status_source.replace('_', ' ')})`
    : '';
  switch (effectiveStatus) {
    case 'activating':
      return `Activation in progress (20s warmup)${source}`;
    case 'handing_over':
      return `Handover countdown — switching soon${source}`;
    case 'active':
      return entry.show_30s_warning
        ? `Switching soon — another activation is queued${source}`
        : `Port is active on hardware${source}`;
    case 'queued':
      return `Waiting in activation queue${source}`;
    case 'error':
      return `Activation failed${source}`;
    default:
      return `Port is inactive${source}`;
  }
}

export const ACTIVATION_POLL_INTERVAL_MS = 30000;
